import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import type { PracticeUser } from "../types";
import { esc } from "../utils/html";

let users: PracticeUser[] = [];
let currentEl: HTMLElement | null = null;

const ROLE_LABELS: Record<string, string> = {
  "praktikumsstelle-admin": "Admin",
  "praktikumsstelle": "Mitarbeiter",
};

async function load() {
  users = await api.practiceUsers.list();
}

(window as any).__puEdit = (id: string) => {
  try {
    const u = users.find(u => u.id === id);
    if (u) openForm(u);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__puToggle = async (id: string) => {
  try {
    const u = users.find(u => u.id === id);
    if (!u) return;
    await api.practiceUsers.toggle(id, !u.enabled);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__puResetPw = (id: string) => {
  try {
    const u = users.find(u => u.id === id);
    if (u) openResetPasswordModal(u);
  } catch (err: any) { alert("Fehler: " + err.message); }
};
(window as any).__puDel = async (id: string) => {
  try {
    if (!confirm("Benutzer wirklich löschen?")) return;
    await api.practiceUsers.delete(id);
    await load();
    if (currentEl) renderTable(currentEl);
  } catch (err: any) { alert("Fehler: " + err.message); }
};

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("tbody")!;
  if (users.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="empty-state">Keine Benutzer vorhanden</td></tr>`;
    return;
  }
  tbody.innerHTML = users.map(u => `
    <tr style="cursor:pointer" onclick="window.__puEdit('${esc(u.id)}')">
      <td onclick="event.stopPropagation()"><input type="checkbox" class="row-sel" data-id="${esc(u.id)}" onchange="window.__puUpdateSel()" /></td>
      <td style="font-weight:500">${esc(u.firstName)} ${esc(u.lastName)}</td>
      <td class="text-muted">${esc(u.email)}</td>
      <td><span class="badge ${u.role === 'praktikumsstelle-admin' ? 'badge-primary' : ''}">${esc(ROLE_LABELS[u.role] || u.role)}</span></td>
      <td>${u.enabled
        ? `<span style="color:var(--success)">Aktiv</span>`
        : `<span style="color:var(--danger)">Deaktiviert</span>`}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm" onclick="event.stopPropagation();window.__puToggle('${esc(u.id)}')">${u.enabled ? "Deaktivieren" : "Aktivieren"}</button>
        <button class="btn btn-sm" onclick="event.stopPropagation();window.__puResetPw('${esc(u.id)}')">Passwort</button>
      </td>
    </tr>`).join("");
}

function openForm(u?: PracticeUser) {
  const isEdit = !!u;
  const roleOpts = Object.entries(ROLE_LABELS).map(([val, label]) =>
    `<option value="${val}" ${u?.role === val ? "selected" : ""}>${label}</option>`
  ).join("");

  openModal(isEdit ? "Benutzer bearbeiten" : "Neuer Benutzer", `
    <div class="form-grid">
      <div class="form-group"><label>Vorname *</label><input id="f-fn" value="${esc(u?.firstName || "")}" /></div>
      <div class="form-group"><label>Nachname *</label><input id="f-ln" value="${esc(u?.lastName || "")}" /></div>
      <div class="form-group"><label>E-Mail-Adresse *</label><input id="f-email" type="email" value="${esc(u?.email || "")}" /></div>
      <div class="form-group"><label>Rolle</label><select id="f-role">${roleOpts}</select></div>
      ${!isEdit ? `<div class="form-group" style="grid-column:1/-1"><p style="font-size:.875rem;color:var(--gray-600);margin:0">Der Benutzer erhält nach dem Anlegen eine E-Mail zum Setzen seines Passworts.</p></div>` : ""}
    </div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">Speichern</button></div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const email = (mb.querySelector("#f-email") as HTMLInputElement).value.trim();
    const firstName = (mb.querySelector("#f-fn") as HTMLInputElement).value.trim();
    const lastName = (mb.querySelector("#f-ln") as HTMLInputElement).value.trim();
    const role = (mb.querySelector("#f-role") as HTMLSelectElement).value;

    if (!email) { alert("E-Mail ist ein Pflichtfeld"); return; }
    if (!firstName) { alert("Vorname ist ein Pflichtfeld"); return; }
    if (!lastName) { alert("Nachname ist ein Pflichtfeld"); return; }

    try {
      if (isEdit) {
        await api.practiceUsers.update(u!.id, { email, firstName, lastName, role });
      } else {
        const result = await api.practiceUsers.create({ email, firstName, lastName, role });
        closeModal(); await load(); if (currentEl) renderTable(currentEl);
        if (result && (result as any).emailSent === false) {
          alert("Benutzer wurde erfolgreich erstellt. Bitte sende dem Benutzer über den \"Passwort\"-Button in der Tabelle eine E-Mail, damit er sein Passwort setzen kann.");
        }
        return;
      }
      closeModal(); await load(); if (currentEl) renderTable(currentEl);
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

function openResetPasswordModal(u: PracticeUser) {
  openModal("Passwort zurücksetzen", `
    <p>Es wird eine E-Mail an <strong>${esc(u.email)}</strong> gesendet, damit der Benutzer sich ein neues Passwort setzen kann.</p>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">Senden</button></div>`);

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    try {
      const result = await api.practiceUsers.resetPassword(u.id);
      if (result && (result as any).tempPassword) {
        mb.innerHTML = `
          <p>Die E-Mail konnte nicht gesendet werden. Bitte teile dem Benutzer das folgende temporäre Passwort mit. Der Benutzer muss es beim nächsten Login ändern.</p>
          <div style="background:var(--gray-100);padding:.75rem 1rem;border-radius:.5rem;font-family:monospace;font-size:1.1rem;letter-spacing:.05em;margin:.5rem 0">${esc((result as any).tempPassword)}</div>
          <div class="form-actions"><button class="btn btn-primary" id="f-close">Schließen</button></div>`;
        mb.querySelector("#f-close")!.addEventListener("click", closeModal);
      } else {
        closeModal();
      }
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `<div class="page-header"><h1>Benutzer</h1><div style="display:flex;gap:.5rem"><button class="btn btn-danger btn-sm" id="btn-del-sel-u" disabled>Auswahl löschen</button><button class="btn btn-primary" id="btn-add">+ Neuer Benutzer</button></div></div>
    <div class="card table-wrap"><table><thead><tr><th style="width:36px"><input type="checkbox" id="sel-all-u" style="width:16px;height:16px;accent-color:var(--primary);cursor:pointer" /></th><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th style="width:200px">Aktionen</th></tr></thead><tbody></tbody></table></div>`;
  el.querySelector("#btn-add")!.addEventListener("click", () => openForm());
  await load();
  renderTable(el);

  const updateSelU = () => {
    const boxes = [...el.querySelectorAll<HTMLInputElement>(".row-sel")];
    const checked = boxes.filter(b => b.checked);
    const selAll = el.querySelector<HTMLInputElement>("#sel-all-u");
    const delBtn = el.querySelector<HTMLButtonElement>("#btn-del-sel-u");
    if (selAll) { selAll.checked = checked.length === boxes.length && boxes.length > 0; selAll.indeterminate = checked.length > 0 && checked.length < boxes.length; }
    if (delBtn) delBtn.disabled = checked.length === 0;
  };
  (window as any).__puUpdateSel = updateSelU;
  el.querySelector<HTMLInputElement>("#sel-all-u")?.addEventListener("change", (e) => {
    const cb = e.target as HTMLInputElement;
    el.querySelectorAll<HTMLInputElement>(".row-sel").forEach(b => b.checked = cb.checked);
    updateSelU();
  });
  el.querySelector<HTMLButtonElement>("#btn-del-sel-u")?.addEventListener("click", async () => {
    const ids = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => b.dataset.id!);
    if (!ids.length) return;
    if (!confirm(`${ids.length} Benutzer löschen?`)) return;
    try { await Promise.all(ids.map(id => api.practiceUsers.delete(id))); await load(); renderTable(el); }
    catch (err: any) { alert("Fehler: " + err.message); }
  });
}
